import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import {
  SINGLE_OUTPUT_NAME,
  SingleOutputWorkflowComponent
} from './common/singleoutputcomponent'

class Delaunay extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.delaunay',
        group: 'Spatial Analysis',
        title: 'ST Delaunay Polygons',
        description: `
      This component computes a Delaunay triangulation based in a input table with points.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'lines',
          type: WorkflowComponentParamType.Boolean,
          title: 'Return lines',
          default: true
        }
      ]
    )
    this.supportedProviders = [
      Provider.BigQuery,
      Provider.Snowflake,
      Provider.Redshift,
      Provider.Postgres
    ]
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const preparedParams = this._prepareParameters(params)
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    let fn = 'ST_DELAUNAYPOLYGONS'
    if (params.lines ?? true) {
      fn = 'ST_DELAUNAYLINES'
    }
    let query = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        const select = `SELECT geom
                      FROM (
                        SELECT ${connection.quoteName(
                          envVars.analyticsToolboxDataset as string
                        )}.${fn}(pts) geo
                        FROM (
                          SELECT ARRAY_AGG(${preparedParams.geo}) pts
                          FROM ${connection.quoteName(
                            preparedParams.source as string
                          )}
                        ) aggregated
                      ) triangulation, UNNEST(triangulation.geo) geom`
        query = connection.getCreateTempTableQuery(
          tablename,
          select,
          codeType,
          null
        )
        break
      }
      case Provider.Snowflake: {
        if (dryRun) {
          query = connection.getCreateTempTableQuery(
            tablename,
            `SELECT ${preparedParams.geo} AS geom
            FROM ${connection.quoteName(preparedParams.source as string)}`,
            codeType,
            null
          )
        } else {
          const tableSelect = `WITH triangulation AS (
                              SELECT ${connection.quoteName(
                                envVars.analyticsToolboxDataset as string
                              )}.${fn}(:pts) geo
                            )
                            SELECT TO_GEOGRAPHY(VALUE) AS geom
                            FROM triangulation, LATERAL FLATTEN(input => geo) geom`
          const createTableQuery = connection.getCreateTempTableQuery(
            tablename,
            tableSelect,
            codeType,
            null
          )
          query = `LET pts ARRAY;
                SELECT
                  ARRAY_AGG(ST_ASGEOJSON(GEOM)::STRING) pts
                INTO
                  pts
                FROM
                  ${connection.quoteName(preparedParams.source as string)};
                ${createTableQuery}`
        }
        break
      }
      case Provider.Postgres: {
        const select = `WITH triangulation AS (
                        SELECT
                          ST_DELAUNAYTRIANGLES(
                            ST_COLLECT(${preparedParams.geo}),
                            0.001,
                            ${preparedParams.lines ?? true ? 1 : 0}
                          ) geom
                        FROM ${connection.quoteName(
                          preparedParams.source as string
                        )}
                      )
                      SELECT (ST_DUMP(geom)).geom geom
                      FROM triangulation`
        query = connection.getCreateTempTableQuery(
          tablename,
          select,
          codeType,
          null
        )
        break
      }
      case Provider.Redshift: {
        const select = `WITH aggregated AS (
                        SELECT
                          ST_COLLECT(${preparedParams.geo}) pts
                        FROM
                          ${connection.quoteName(
                            preparedParams.source as string
                          )}
                      ),
                      triangulation AS (
                        SELECT
                          ST_GEOMFROMGEOJSON(
                            ${connection.quoteName(
                              envVars.analyticsToolboxDataset as string
                            )}.${fn} (pts)
                          ) geo
                        FROM
                          aggregated
                      ),
                      idx AS (
                        SELECT
                          ROW_NUMBER() OVER () i
                        FROM
                          ${connection.quoteName(
                            preparedParams.source as string
                          )}
                      ),
                      alltriangles AS (
                        SELECT ST_GEOMETRYN(geo, i::integer) geom
                        FROM triangulation, idx
                      )
                      SELECT *
                      FROM alltriangles
                      WHERE geom IS NOT NULL`
        query = connection.getCreateTempTableQuery(
          tablename,
          select,
          codeType,
          null
        )
        break
      }
      default:
        return null
    }

    return {
      query: query,
      outputs: { result: tablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ connection }: GetOutputSchemaParams): TableSchema {
    return [
      {
        name: 'geom',
        type: connection.getGeoDataType()
      }
    ]
  }
}

export { Delaunay }
